import React, { useEffect, useState } from "react";
import { Badge, HStack, Text } from "@chakra-ui/react";

/**
 * Last night's run of a check. Each night that had commits, the server re-runs the code review,
 * the memory scan, every memory check and the DB health check (backend/api/nightly_checks.py,
 * public.nightly_check_runs). A tab shows whichever is newer: last night's result or one run from
 * this browser, labelled so it's clear which.
 */

export function serverLabel(server) {
  const text = String(server || "");
  const at = text.indexOf(":");
  const kind = at >= 0 ? text.slice(0, at) : "";
  const name = at >= 0 ? text.slice(at + 1) : text;
  if (kind === "render") return `Render (${name})`;
  if (kind === "local") return name ? `the computer ${name}` : "a local computer";
  return name || "the server";
}

export function useLastNight(kind, fetchLatest) {
  const [state, setState] = useState({ loading: Boolean(fetchLatest) });
  useEffect(() => {
    if (!fetchLatest) return undefined;
    let cancelled = false;
    fetchLatest(kind)
      .then((data) => { if (!cancelled) setState({ run: data.run || null, missing: data.missing || null }); })
      .catch((e) => { if (!cancelled) setState({ error: e?.message || String(e) }); });
    return () => { cancelled = true; };
  }, [kind, fetchLatest]);
  return state;
}

function when(run) {
  return run?.finished_at || run?.started_at || null;
}

/**
 * The newer of a result run from this browser (a cache entry, {data, savedAt}) and last night's
 * run (a nightly_check_runs row): {source: "live" | "nightly", data, at, run?}, or null.
 */
export function pickNewer(entry, run) {
  const liveAt = entry?.data && entry?.savedAt ? Date.parse(entry.savedAt) : NaN;
  const nightAt = run?.status === "ok" && run?.report ? Date.parse(when(run)) : NaN;
  if (!Number.isNaN(nightAt) && (Number.isNaN(liveAt) || nightAt > liveAt)) {
    return { source: "nightly", data: run.report, at: when(run), run };
  }
  if (entry?.data) return { source: "live", data: entry.data, at: entry.savedAt };
  return null;
}

export function LastNightLabel({ run, testId = "last-night", what = null }) {
  // `what` names what ran ("The live checks and the code scan") where one line covers several.
  if (!run) return null;
  const at = when(run);
  const time = at ? new Date(at).toLocaleString([], { weekday: "short", month: "short", day: "numeric",
                                                      hour: "numeric", minute: "2-digit" }) : "";
  if (run.status === "failed") {
    return (
      <Text fontSize="xs" color="orange.700" data-testid={testId}>
        {what ? `${what} didn't finish last night` : "Last night's automatic check didn't finish"} ({time}, on{" "}
        {serverLabel(run.server)}): {run.error}
      </Text>
    );
  }
  return (
    <HStack spacing={2} flexWrap="wrap" data-testid={testId}>
      <Badge colorScheme="purple" variant="subtle">LAST NIGHT</Badge>
      <Text fontSize="xs" color="gray.600">
        {what ? `${what} ran automatically after the day's commits` : "Checked automatically after the day's commits"}:
        {" "}{time}, on {serverLabel(run.server)}.
      </Text>
    </HStack>
  );
}
